import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { toast } from 'sonner';
import { SETTINGS_ACCESS_DENIED_TOAST_ID } from './app-shell-route-utils';
import { useUserManagementAccess } from './useUserManagementAccess';

/**
 * Route-level gate for the User Management pages (scio3#2351).
 *
 * `useUserManagementAccess` governs NAVIGATION — the Settings side panel and the top-bar shortcuts —
 * so a role that may not use a page simply has no link to it. The ROUTES had no gate at all, so the
 * same page still rendered in full for anyone who typed or bookmarked its URL. The API now withholds
 * the sensitive data (open-api!1659), so this is no longer a disclosure, but a non-admin landing on a
 * page of withheld panels reads as a broken app rather than as "not for you".
 *
 * Deliberately a ROUTE wrapper rather than a check inside the page components: `MembersPage` is also
 * rendered as a POP-UP by the Teamspaces page, and a `<Navigate>` fired from inside it would redirect
 * the whole app out from under that dialog.
 *
 * Redirects to the same place `AuditLogPage` does, so an unauthorized deep link behaves consistently
 * across settings.
 *
 * IT SAYS WHY (scio3#2413). This used to redirect in silence, which left the app looking broken
 * rather than closed: the page simply vanished and Screens appeared. `SettingsLayout` already had
 * the right behaviour for its own refusals — toast, then navigate (`accessGate`) — and the two
 * disagreeing was the last open item on #2413.
 *
 * The toast id is `SETTINGS_ACCESS_DENIED_TOAST_ID`, shared with `SettingsLayout` on purpose, so
 * the two paths can never stack two toasts for one refusal. It also makes the effect safe under
 * StrictMode's double-invoke, where an id-less toast would appear twice in development.
 *
 * NOTE this is the only remaining consumer of the guard: #2413 removed it from `teamspaces`,
 * `roles` and `group-security`, whose pages explain plan / activation state themselves. The `area`
 * union keeps the other three names because the hook still exposes them and a future route may
 * legitimately gate on one — but prefer letting the page answer, per that issue.
 */
export function UserManagementRouteGuard({
  area,
  children,
}: {
  /** Which entry of `useUserManagementAccess` governs this route. */
  area: 'usersPage' | 'teamspaces' | 'roles' | 'groups';
  children: ReactNode;
}) {
  const access = useUserManagementAccess();
  const { t } = useTranslation();
  const isDenied = !access[area];

  useEffect(() => {
    if (!isDenied) return;
    toast.error(t('You do not have permission to access this page.'), {
      id: SETTINGS_ACCESS_DENIED_TOAST_ID,
    });
  }, [isDenied, t]);

  if (isDenied) return <Navigate to="/app/screenManagement" replace />;
  return <>{children}</>;
}
